import React from "react";
import { Button } from "@/components/ui/button";
import {
  Loader2, RefreshCw, X, CheckCircle2, AlertTriangle, Sparkles, PenTool,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { MapSvgTracer } from "@/components/map/MapSvgTracer";
import { PannableImage } from "@/components/world/PannableImage";
import type { ImageGenTabProps } from "./types";

export function GenerateTab(props: ImageGenTabProps) {
  const {
    entity, worldId, imageType, imageLabel, dialogTitle, previewAspectRatio,
    generationStep, generationError, generatedPreview, setGeneratedPreview,
    generating, handleGenerate, briefText, generationPrompt,
    stepArtEnabled, stepReferencesEnabled, stepDrawEnabled,
    seedImageUrl, drawingDataUrl, selectedRefImages,
    onImageAccepted, onImageGenerated, onOpenChange,
    setDrawingInitialImage, setActiveTab,
  } = props;

  return (
    <div className="space-y-4">
      {/* Generation Progress */}
      {generationStep && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin text-primary shrink-0" />
            <span className="text-sm font-medium text-foreground">{generationStep}</span>
          </div>
          <div className="pl-7 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="w-1.5 h-1.5 rounded-full bg-primary/60 animate-bounce" style={{ animationDelay: "300ms" }} />
            <span className="text-[11px] text-muted-foreground ml-1.5">This can take up to 60s…</span>
          </div>
        </div>
      )}

      {/* Error */}
      {generationError && !generationStep && !generatedPreview && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
            <span className="text-xs text-foreground">{generationError}</span>
          </div>
          <Button variant="outline" size="sm" className="w-full gap-1.5 text-xs" onClick={() => handleGenerate()}>
            <RefreshCw className="w-3 h-3" /> Retry
          </Button>
        </div>
      )}

      {/* Generated Preview */}
      {generatedPreview && !generationStep && (
        <div className="space-y-3">
          <PannableImage
            src={generatedPreview}
            alt={entity.name}
            containerClassName={cn("rounded-lg overflow-hidden border-2 border-primary/30", previewAspectRatio ? `aspect-[${previewAspectRatio}]` : "")}
            className={cn("w-full", previewAspectRatio ? "h-full object-cover" : "max-h-72 object-contain")}
            style={{ backgroundColor: "hsl(var(--muted) / 0.5)" }}
            onCommitCrop={(cropped) => {
              setGeneratedPreview(cropped);
              toast.success("Crop saved — applied to preview");
            }}
          />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="flex-1 gap-1.5" onClick={() => setGeneratedPreview(null)}>
              <X className="w-3.5 h-3.5" /> Deny
            </Button>
            <Button size="sm" className="flex-1 gap-1.5" onClick={() => {
              if (onImageAccepted) onImageAccepted(generatedPreview);
              else onImageGenerated?.(entity.id, generatedPreview);
              toast.success(`Image accepted for ${entity.name}`);
              onOpenChange(false);
            }}>
              <CheckCircle2 className="w-3.5 h-3.5" /> Accept
            </Button>
          </div>
          {imageType === "map" && (
            <Button variant="outline" size="sm" className="w-full gap-1.5 text-xs" onClick={() => {}}>
              <PenTool className="w-3 h-3" /> Convert to SVG
            </Button>
          )}
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" className="flex-1 gap-1.5 text-xs text-muted-foreground" onClick={handleGenerate} disabled={generating}>
              <RefreshCw className="w-3 h-3" /> Regenerate
            </Button>
            <Button variant="ghost" size="sm" className="flex-1 gap-1.5 text-xs text-muted-foreground" onClick={() => { setDrawingInitialImage(generatedPreview); setActiveTab("draw"); }}>
              <PenTool className="w-3 h-3" /> Edit in Draw
            </Button>
          </div>
        </div>
      )}

      {/* Generate button */}
      {!generationStep && !generatedPreview && !generationError && (
        <div className="space-y-3">
          <div className="rounded-lg bg-muted/30 border border-border p-3 space-y-1.5">
            <p className="text-[11px] font-medium text-foreground">Ready to generate</p>
            <ul className="text-[11px] text-muted-foreground space-y-0.5 pl-5 list-disc">
              {!stepArtEnabled && <li className="text-muted-foreground/50 line-through">Art direction (disabled)</li>}
              {stepArtEnabled && briefText.trim() ? <li>Brief: {briefText.length} chars</li> : stepArtEnabled && <li className="text-amber-500">No brief — go to Art Direction or Research first</li>}
              {stepArtEnabled && generationPrompt.trim() && <li>Art direction included</li>}
              {!stepReferencesEnabled && <li className="text-muted-foreground/50 line-through">References (disabled)</li>}
              {!stepDrawEnabled && <li className="text-muted-foreground/50 line-through">Drawing (disabled)</li>}
              {stepDrawEnabled && drawingDataUrl && <li>Drawing sketch as seed</li>}
              {stepReferencesEnabled && seedImageUrl && !(stepDrawEnabled && drawingDataUrl) && <li>Seed reference image</li>}
              {stepReferencesEnabled && Array.from(selectedRefImages).filter(u => u !== seedImageUrl).length > 0 && (
                <li>{Array.from(selectedRefImages).filter(u => u !== seedImageUrl).length} additional reference(s)</li>
              )}
            </ul>
          </div>
          <Button onClick={handleGenerate} disabled={(!briefText.trim() && !generationPrompt.trim() && !stepReferencesEnabled) || generating} className="w-full gap-2 h-10">
            <Sparkles className="w-4 h-4" /> Generate {dialogTitle ? "Image" : imageLabel}
          </Button>
        </div>
      )}
    </div>
  );
}
